import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, ChevronLeft, ChevronRight, Eye, LogIn, LogOut, Printer, Search, Waves, X } from 'lucide-react';
import { Colonia } from '../../types/colonia';
import AdminTopbar from './AdminTopbar';
import { getAvailableAdminPeriods, getCurrentAdminPeriod, getInscricaoYear, useAdminInscricoesData } from './adminData';
import { AcaoPendente, CampoPresenca, carregarFilaPendente, marcarPresenca, salvarFilaPendente } from './chamadaData';
import FichaAlunoModal, { textoLiberacaoPiscina } from './FichaAlunoModal';
import FotoAmpliadaModal from './FotoAmpliadaModal';
import './AdminPanel.css';

interface PresenceState {
  entrada: boolean;
  saida: boolean;
  piscina?: 'sim' | 'nao';
  horarioEntrada?: string;
  horarioSaida?: string;
}

const adminApiBaseUrl =
  process.env.REACT_APP_PAYMENT_API_URL ||
  'https://southamerica-east1-coloniadeferiasss.cloudfunctions.net/paymentApi';

/** Intervalo do "tempo real": não é um listener de verdade, só reconsulta com frequência
 * — suficiente pra quem tá acompanhando ver a chamada da portaria em poucos segundos. */
const INTERVALO_TEMPO_REAL_MS = 4000;
/** De quanto em quanto tempo tenta sincronizar a fila pendente (rede caiu, offline etc.). */
const INTERVALO_RETENTATIVA_MS = 5000;

const todayInputValue = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const normalizeText = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0])
    .join('')
    .toUpperCase() || '?';

const formatarDataLonga = (valor: string) => {
  const data = new Date(`${valor}T00:00:00`);
  if (Number.isNaN(data.getTime())) return valor;
  return new Intl.DateTimeFormat('pt-BR', { weekday: 'short', day: '2-digit', month: 'short' }).format(data);
};

const normalizePresencePayload = (value: unknown): Record<string, PresenceState> => {
  if (!value) return {};

  if (Array.isArray(value)) {
    return Object.fromEntries(
      value
        .filter((item) => item && typeof item === 'object')
        .map((item: any) => [
          String(item.id || item.inscricaoId || item.nome || item.nomeCompleto),
          {
            entrada: Boolean(item.entrada ?? item.presente),
            saida: Boolean(item.saida),
            piscina: item.piscina === 'sim' || item.piscina === true ? 'sim' : item.piscina === 'nao' ? 'nao' : undefined,
            horarioEntrada: item.horarioEntrada,
            horarioSaida: item.horarioSaida
          }
        ])
        .filter(([key]) => key && key !== 'undefined')
    );
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, any>).map(([key, item]) => [
        key,
        {
          entrada: Boolean(item?.entrada),
          saida: Boolean(item?.saida),
          piscina: item?.piscina === 'sim' || item?.piscina === 'nao' ? item.piscina : undefined,
          horarioEntrada: item?.horarioEntrada,
          horarioSaida: item?.horarioSaida
        }
      ])
    );
  }

  return {};
};

/**
 * Reaplica por cima do que veio do servidor qualquer marcação ainda pendente de sincronizar
 * (do dia atualmente exibido) — assim uma reconsulta em segundo plano nunca "desmarca" na tela
 * um toque que o usuário acabou de dar e que ainda não confirmou no servidor (ex.: offline).
 */
const aplicarPendentesSobre = (
  base: Record<string, PresenceState>,
  fila: AcaoPendente[],
  dataAtual: string
): Record<string, PresenceState> => {
  const resultado = { ...base };
  fila
    .filter((acao) => acao.data === dataAtual)
    .forEach((acao) => {
      const atual = resultado[acao.inscricaoId] || { entrada: false, saida: false };
      if (acao.campo === 'entrada') {
        resultado[acao.inscricaoId] = { ...atual, entrada: acao.valor, horarioEntrada: acao.valor ? acao.horario : undefined };
      } else if (acao.campo === 'saida') {
        resultado[acao.inscricaoId] = { ...atual, saida: acao.valor, horarioSaida: acao.valor ? acao.horario : undefined };
      } else {
        resultado[acao.inscricaoId] = { ...atual, piscina: acao.valor ? 'sim' : undefined };
      }
    });
  return resultado;
};

const ChamadaRapida: React.FC<{ publico?: boolean }> = ({ publico }) => {
  const navigate = useNavigate();
  const { inscricoes, loading, error } = useAdminInscricoesData();
  const [selectedYear, setSelectedYear] = useState(getCurrentAdminPeriod);
  const [dataChamada, setDataChamada] = useState(todayInputValue());
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroTurma, setFiltroTurma] = useState('todos');
  const [presencas, setPresencas] = useState<Record<string, PresenceState>>({});
  const [chamadaStatus, setChamadaStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [filaPendente, setFilaPendente] = useState<AcaoPendente[]>(() => carregarFilaPendente());
  const [detalhesAluno, setDetalhesAluno] = useState<Colonia | null>(null);
  const [fotoAmpliada, setFotoAmpliada] = useState<{ tipo: 'aluno' | 'responsavel'; inscricao: Colonia } | null>(null);

  const availableYears = useMemo(() => getAvailableAdminPeriods(inscricoes), [inscricoes]);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // A fila é a fonte da verdade do que ainda falta sincronizar — persiste a cada mudança pra
  // sobreviver a um reload no meio de uma queda de conexão.
  const filaPendenteRef = useRef(filaPendente);
  useEffect(() => {
    filaPendenteRef.current = filaPendente;
    salvarFilaPendente(filaPendente);
  }, [filaPendente]);

  const enviandoRef = useRef(false);

  const processarFilaPendente = async () => {
    if (enviandoRef.current) return;
    enviandoRef.current = true;
    try {
      for (const acao of filaPendenteRef.current) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await marcarPresenca(acao.data, acao.inscricaoId, acao.campo, acao.valor, acao.horario);
          setFilaPendente((atual) => atual.filter((item) => item.chave !== acao.chave));
        } catch {
          // Provavelmente ainda offline: para por aqui e tenta tudo de novo na próxima
          // rodada, em vez de queimar as outras marcações fora de ordem.
          break;
        }
      }
    } finally {
      enviandoRef.current = false;
    }
  };

  // Tenta sincronizar a fila ao montar (ex.: reload com pendências de uma queda anterior),
  // sempre que a conexão voltar, e periodicamente como rede de segurança.
  useEffect(() => {
    const tentar = () => {
      void processarFilaPendente();
    };
    tentar();
    window.addEventListener('online', tentar);
    const intervalo = setInterval(tentar, INTERVALO_RETENTATIVA_MS);
    return () => {
      window.removeEventListener('online', tentar);
      clearInterval(intervalo);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;

    // `silencioso` é usado pelo polling em segundo plano: não pisca "Carregando..." a cada
    // reconsulta e não apaga a lista se uma reconsulta falhar. Em qualquer dos dois casos,
    // qualquer marcação ainda pendente de sincronizar é reaplicada por cima da resposta do
    // servidor — nunca "some" da tela um toque que ainda não foi confirmado.
    const loadChamada = async (silencioso: boolean) => {
      if (!silencioso) setChamadaStatus('loading');
      try {
        const response = await fetch(`${adminApiBaseUrl.replace(/\/$/, '')}/admin/chamadas?data=${encodeURIComponent(dataChamada)}`);
        if (!response.ok) throw new Error('chamada_unavailable');
        const payload = await response.json();
        if (!active) return;
        const doServidor = normalizePresencePayload(payload.chamada?.presencas);
        setPresencas(aplicarPendentesSobre(doServidor, filaPendenteRef.current, dataChamada));
        setChamadaStatus('idle');
      } catch (loadError) {
        if (!active) return;
        if (!silencioso) setPresencas((atual) => aplicarPendentesSobre(atual, filaPendenteRef.current, dataChamada));
        setChamadaStatus('error');
      }
    };

    loadChamada(false);
    const intervalo = setInterval(() => loadChamada(true), INTERVALO_TEMPO_REAL_MS);

    return () => {
      active = false;
      clearInterval(intervalo);
    };
  }, [dataChamada]);

  const yearInscricoes = useMemo(
    () =>
      inscricoes
        .filter((inscricao) => getInscricaoYear(inscricao) === selectedYear)
        .sort((a, b) => a.nomeCompleto.localeCompare(b.nomeCompleto, 'pt-BR')),
    [inscricoes, selectedYear]
  );

  const turmas = useMemo(
    () => Array.from(new Set(yearInscricoes.map((item) => item.turma || 'Sem turma'))).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    [yearInscricoes]
  );

  const alunosFiltrados = useMemo(() => {
    const busca = normalizeText(filtroNome);
    return yearInscricoes.filter((item) => {
      const nomeMatch = !busca || normalizeText(item.nomeCompleto).includes(busca);
      const turmaMatch = filtroTurma === 'todos' || (item.turma || 'Sem turma') === filtroTurma;
      return nomeMatch && turmaMatch;
    });
  }, [filtroNome, filtroTurma, yearInscricoes]);

  const chaveDe = (item: Colonia) => item.id || item.nomeCompleto;
  const presentes = yearInscricoes.filter((item) => presencas[chaveDe(item)]?.entrada).length;
  const saidas = yearInscricoes.filter((item) => presencas[chaveDe(item)]?.saida).length;
  const naPiscina = yearInscricoes.filter((item) => presencas[chaveDe(item)]?.piscina === 'sim').length;
  const faltamChegar = yearInscricoes.length - presentes;
  const percentual = yearInscricoes.length ? Math.round((presentes / yearInscricoes.length) * 100) : 0;

  const mudarData = (offset: number) => {
    const date = new Date(`${dataChamada}T00:00:00`);
    date.setDate(date.getDate() + offset);
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    setDataChamada(date.toISOString().slice(0, 10));
  };

  const currentTime = () => new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  /**
   * Marca só o campo tocado (nunca reenvia a lista inteira do dia): atualiza a tela na hora,
   * guarda a intenção na fila local e tenta sincronizar — se estiver offline, fica pendente e
   * a rede de segurança (evento "online" + retentativa periódica) manda assim que possível.
   */
  const marcarCampo = (inscricao: Colonia, campo: CampoPresenca) => {
    const key = chaveDe(inscricao);
    const atual = presencas[key] || { entrada: false, saida: false };

    let valor: boolean;
    let horario: string | undefined;
    let proximoEstado: PresenceState;

    if (campo === 'entrada') {
      valor = !atual.entrada;
      horario = valor ? currentTime() : undefined;
      proximoEstado = { ...atual, entrada: valor, horarioEntrada: horario };
    } else if (campo === 'saida') {
      valor = !atual.saida;
      horario = valor ? currentTime() : undefined;
      proximoEstado = { ...atual, saida: valor, horarioSaida: horario };
    } else {
      valor = atual.piscina !== 'sim';
      proximoEstado = { ...atual, piscina: valor ? 'sim' : undefined };
    }

    setPresencas((current) => ({ ...current, [key]: proximoEstado }));

    const acao: AcaoPendente = {
      chave: `${dataChamada}:${key}:${campo}`,
      data: dataChamada,
      inscricaoId: key,
      campo,
      valor,
      horario,
      criadoEm: Date.now()
    };
    setFilaPendente((atual2) => [...atual2.filter((item) => item.chave !== acao.chave), acao]);
    setTimeout(() => void processarFilaPendente(), 0);
  };

  const imprimirChamada = () => window.print();

  return (
    <div className="legacy-admin quick-call-page">
      <AdminTopbar
        inscricoes={inscricoes}
        selectedYear={selectedYear}
        title="Chamada Rápida"
        onYearChange={setSelectedYear}
        publico={publico}
      >
        <span className="quick-missing-badge">
          {faltamChegar} {faltamChegar === 1 ? 'falta' : 'faltam'} chegar
        </span>
        <div className="quick-date-control">
          <button type="button" aria-label="Dia anterior" onClick={() => mudarData(-1)}>
            <ChevronLeft size={22} />
          </button>
          <label>
            <CalendarDays size={18} />
            <span className="quick-date-text">{formatarDataLonga(dataChamada)}</span>
            <input
              type="date"
              value={dataChamada}
              aria-label="Data da chamada"
              onChange={(event) => setDataChamada(event.target.value)}
            />
          </label>
          <button type="button" aria-label="Próximo dia" onClick={() => mudarData(1)}>
            <ChevronRight size={22} />
          </button>
        </div>
      </AdminTopbar>

      <main className="quick-content">
        <div className="quick-toolbar">
          {!publico && (
            <button className="legacy-top-button outline dark quick-back" type="button" onClick={() => navigate('/gerenciamento-colonia')}>
              ← Voltar
            </button>
          )}

          <div className="quick-toolbar-end">
            {filaPendente.length > 0 ? (
              <span className="quick-save-status saving">
                {filaPendente.length} {filaPendente.length === 1 ? 'marcação pendente' : 'marcações pendentes'}
              </span>
            ) : (
              <span className={`quick-save-status ${chamadaStatus}`}>
                {chamadaStatus === 'loading' && 'Carregando...'}
                {chamadaStatus === 'idle' && 'Tudo sincronizado'}
                {chamadaStatus === 'error' && 'Sem conexão com o servidor'}
              </span>
            )}
            <button className="legacy-action-button green quick-print" type="button" onClick={imprimirChamada}>
              <Printer size={15} />
              <span>Imprimir</span>
            </button>
          </div>
        </div>

        <section className="quick-stats">
          <div className="quick-stat-row">
            <div className="quick-stat">
              <span>Total</span>
              <strong>{yearInscricoes.length}</strong>
            </div>
            <div className="quick-stat green">
              <span>Entradas</span>
              <strong>{presentes}</strong>
            </div>
            <div className="quick-stat orange">
              <span>Saídas</span>
              <strong>{saidas}</strong>
            </div>
            <div className="quick-stat blue">
              <span>Piscina</span>
              <strong>{naPiscina}</strong>
            </div>
          </div>
          <div className="quick-progress" aria-label={`${percentual}% presentes`}>
            <div className="quick-progress-bar" style={{ width: `${percentual}%` }} />
          </div>
          <small className="quick-progress-label">{presentes} de {yearInscricoes.length} presentes ({percentual}%)</small>
        </section>

        <section className="quick-filters">
          <label className="quick-search">
            <Search size={18} />
            <input
              placeholder="Buscar aluno..."
              value={filtroNome}
              onChange={(event) => setFiltroNome(event.target.value)}
            />
            {filtroNome && (
              <button type="button" aria-label="Limpar busca" onClick={() => setFiltroNome('')}>
                <X size={16} />
              </button>
            )}
          </label>
          <div className="quick-chips">
            <button
              type="button"
              className={filtroTurma === 'todos' ? 'active' : ''}
              onClick={() => setFiltroTurma('todos')}
            >
              Todas <em>{yearInscricoes.length}</em>
            </button>
            {turmas.map((turma) => (
              <button
                type="button"
                key={turma}
                className={filtroTurma === turma ? 'active' : ''}
                onClick={() => setFiltroTurma(turma)}
              >
                {turma} <em>{yearInscricoes.filter((item) => (item.turma || 'Sem turma') === turma).length}</em>
              </button>
            ))}
          </div>
        </section>

        {loading && <div className="legacy-message">Carregando lista de chamada...</div>}
        {error && <div className="legacy-message error">{error}</div>}

        {!loading && !error && alunosFiltrados.length === 0 && (
          <div className="legacy-message">Nenhum aluno encontrado com esses filtros.</div>
        )}

        {!loading && !error && (
          <section className="quick-list">
            {alunosFiltrados.map((inscricao) => {
              const key = chaveDe(inscricao);
              const state = presencas[key] || { entrada: false, saida: false };

              return (
                <article className={`quick-item ${state.entrada ? 'present' : ''} ${state.saida ? 'left' : ''}`} key={key}>
                  <div className="quick-photos">
                    {inscricao.fotoUrl ? (
                      <img
                        className="quick-photo"
                        src={inscricao.fotoUrl}
                        alt=""
                        loading="lazy"
                        onClick={() => setFotoAmpliada({ tipo: 'aluno', inscricao })}
                      />
                    ) : (
                      <span
                        className="quick-photo quick-avatar"
                        role="button"
                        tabIndex={0}
                        onClick={() => setFotoAmpliada({ tipo: 'aluno', inscricao })}
                      >
                        {iniciais(inscricao.nomeCompleto)}
                      </span>
                    )}
                    {inscricao.fotoUrlRetirada ? (
                      <img
                        className="quick-photo quick-photo-responsavel"
                        src={inscricao.fotoUrlRetirada}
                        alt=""
                        loading="lazy"
                        onClick={() => setFotoAmpliada({ tipo: 'responsavel', inscricao })}
                      />
                    ) : (
                      <span
                        className="quick-photo quick-photo-responsavel quick-avatar"
                        role="button"
                        tabIndex={0}
                        onClick={() => setFotoAmpliada({ tipo: 'responsavel', inscricao })}
                      >
                        {iniciais(inscricao.nomeRetirada || inscricao.nomeMae)}
                      </span>
                    )}
                  </div>
                  <div className="quick-item-identity">
                    <strong>{inscricao.nomeCompleto}</strong>
                    <small>{inscricao.nomeMae}</small>
                    <span className="quick-item-turma">{inscricao.turma || 'Sem turma'}</span>
                  </div>

                  <div className="quick-item-actions">
                    <button
                      className={`quick-toggle entrada ${state.entrada ? 'active' : ''}`}
                      type="button"
                      onClick={() => marcarCampo(inscricao, 'entrada')}
                    >
                      <LogIn size={18} />
                      <span>Entrou</span>
                      {state.horarioEntrada && <em>{state.horarioEntrada}</em>}
                    </button>
                    <button
                      className={`quick-toggle saida ${state.saida ? 'active' : ''}`}
                      type="button"
                      onClick={() => marcarCampo(inscricao, 'saida')}
                    >
                      <LogOut size={18} />
                      <span>Saiu</span>
                      {state.horarioSaida && <em>{state.horarioSaida}</em>}
                    </button>
                    <button
                      className={`quick-toggle piscina ${state.piscina === 'sim' ? 'active' : ''} ${
                        inscricao.autorizacaoPiscina === 'nao_autorizado' ? 'bloqueado' : ''
                      }`}
                      type="button"
                      disabled={inscricao.autorizacaoPiscina === 'nao_autorizado'}
                      onClick={() => marcarCampo(inscricao, 'piscina')}
                    >
                      <Waves size={18} />
                      <span>{textoLiberacaoPiscina(inscricao.autorizacaoPiscina)}</span>
                    </button>
                    <button className="quick-toggle detalhes" type="button" onClick={() => setDetalhesAluno(inscricao)}>
                      <Eye size={18} />
                      <span>Ver detalhes</span>
                    </button>
                  </div>
                </article>
              );
            })}
          </section>
        )}
      </main>

      {detalhesAluno && <FichaAlunoModal inscricao={detalhesAluno} onClose={() => setDetalhesAluno(null)} />}
      {fotoAmpliada && (
        <FotoAmpliadaModal
          tipo={fotoAmpliada.tipo}
          inscricao={fotoAmpliada.inscricao}
          onClose={() => setFotoAmpliada(null)}
        />
      )}
    </div>
  );
};

export default ChamadaRapida;
